import React, { useEffect, useState } from 'react'
import Breadcrum from '../../components/breadcrum'
import AddItem from '../../components/addItem'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getCleanPdfName, handleDate } from '../../handleDateAndPdf'
import { FaArrowLeft, FaEdit, FaEye, FaEyeSlash } from 'react-icons/fa'
import {
  deleteCompany,
  deleteFunding,
  getFundingByCompanyId,
  getHideFundingByCompanyId,
  restoreReport,
  sendMail,
  sendMailCompany,
} from '../../api/fundingApi'
import Swal from 'sweetalert2'

const FundingReport = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [showHidden, setShowHidden] = useState(false)
  const queryClient = useQueryClient()
  const [toDelete, setToDelete] = useState(null)
  const [sendingMail, setSendingMail] = useState(false)
  const [deletingCompany, setDeletingCompany] = useState(false)

  useEffect(() => {
    document.title = 'Funding Report'
  }, [])

  const {
    data: funding365Data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['funding365', showHidden],
    queryFn: () =>
      showHidden ? getHideFundingByCompanyId(id) : getFundingByCompanyId(id),

    onError: (error) => {
      toast.error('Error Fetching funding365')
    },
  })

  // Send mail mutation with improved loading state
  const sendMailMutation = useMutation({
    mutationFn: (id) => (deletingCompany ? sendMailCompany(id) : sendMail(id)),
    onMutate: () => {
      setSendingMail(true)
    },
    onSuccess: (response) => {
      setSendingMail(false)

      // Check if the response indicates success
      if (response?.status === 200 || response?.data) {
        toast.success(response?.data)
        // Show OTP modal after successful email send
        showOTPModal()
      } else {
        toast.error('Failed to send verification email')
        setToDelete(null)
      }
    },
    onError: (err) => {
      setSendingMail(false)
      console.error('Send mail error:', err)
      toast.error(err.message || 'Failed to send verification email')
      setToDelete(null)
    },
  })

  // Delete mutation with improved success handling
  const deleteMutation = useMutation({
    mutationFn: ({ id, otp }) =>
      deletingCompany ? deleteCompany(id, otp) : deleteFunding(id, otp),
    onSuccess: (response) => {
      // Invalidate and refetch funding365s
      queryClient.invalidateQueries({ queryKey: ['funding365'] })

      // Show success message
      toast.success('funding365 hide successfully!')
      // Additional success message with SweetAlert
      Swal.fire({
        title: 'Hide!',
        text: 'The funding365 has been hide successfully.',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        timer: 3000,
        timerProgressBar: true,
      })

      // Reset state
      setToDelete(null)
      deleteCompany && navigate('/dealdashboard/funding')
    },
    onError: (err) => {
      console.error('Hide error:', err)
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to hide funding365. Please check your OTP.'
      toast.error(errorMessage)
    },
  })

  // Show OTP modal using SweetAlert with better UX
  const showOTPModal = () => {
    Swal.fire({
      title: 'Enter OTP',
      html: `
        <p>Please enter the OTP sent to your email</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #f0f9ff; border-radius: 8px; border-left: 4px solid #3b82f6;">
          <small style="color: #1e40af;">
            <i class="fas fa-info-circle"></i> Check your email inbox and spam folder
          </small>
        </div>
      `,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        placeholder: 'Enter 6-digit OTP',
        maxlength: 6,
        style: 'text-align: center; font-size: 18px; letter-spacing: 2px;',
      },
      showCancelButton: true,
      confirmButtonText: 'Hide funding365',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      inputValidator: (value) => {
        if (!value) {
          return 'Please enter OTP'
        }
        if (value.length !== 6) {
          return 'OTP must be 6 digits'
        }
        if (!/^\d+$/.test(value)) {
          return 'OTP must contain only numbers'
        }
      },
      preConfirm: (otp) => {
        return new Promise((resolve, reject) => {
          // Show loading state
          Swal.showLoading()

          // Update confirm button text while loading
          const confirmButton = Swal.getConfirmButton()
          const originalText = confirmButton.textContent
          confirmButton.textContent = 'Hiding...'

          // Call delete mutation with OTP
          deleteMutation.mutate(
            { id: toDelete, otp },
            {
              onSuccess: () => {
                resolve()
                Swal.close()
              },
              onError: (error) => {
                confirmButton.textContent = originalText
                Swal.hideLoading()
                const errorMessage =
                  error?.response?.data?.message ||
                  error?.message ||
                  'Invalid OTP or deletion failed'
                Swal.showValidationMessage(`❌ ${errorMessage}`)
                reject(error)
              },
            }
          )
        })
      },
    })
      .then((result) => {
        if (result.dismiss === Swal.DismissReason.cancel) {
          setToDelete(null)
          toast.info('Hide cancelled')
        }
      })
      .catch(() => {
        // Handle any unexpected errors
        setToDelete(null)
      })
  }

  // Handle delete click with improved UX
  const handleDeleteClick = (funding365Id, funding365Title) => {
    setToDelete(funding365Id)

    // Show confirmation dialog first
    Swal.fire({
      title: 'Hide funding365?',
      html: `
        <p>Are you sure you want to hide this funding365?</p>
        <div style="margin: 10px 0; padding: 10px; background-color: #fef2f2; border-radius: 8px; border-left: 4px solid #ef4444;">
          <strong style="color: #dc2626;">${funding365Title}</strong>
        </div>
        <p style="color: #6b7280; font-size: 14px;">
          This action will send an OTP to your email for verification.
        </p>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, Send OTP',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        // Send email for OTP
        sendMailMutation.mutate(funding365Id)
      } else {
        setToDelete(null)
      }
    })
  }

  const restoreMutation = useMutation({
    mutationFn: (id) => restoreReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['funding365'] })
      toast.success('Report restore sucessfully!')
    },
    onError: (err) => {
      console.error('restore report error:', err)
      toast.error(err.message || 'Failed to send verification email')
    },
  })

  if (isLoading) {
    return (
      <>
        <Breadcrum heading="Insight" subheading="funding365" />
        <div className="text-center py-8">Loading funding365...</div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum heading="Insight" subheading="funding365" />
        <div className="text-center py-8 text-red-500">
          Error loading funding365. Please try again later.
        </div>
      </>
    )
  }

  const handleToggle = () => {
    setShowHidden((prev) => !prev)
  }

  // const toggleDeleteCompany = () => {
  //   setDeletingCompany((prev) => !prev)
  // }

  return (
    <>
      <Breadcrum subheading="Funding365 Report" heading="Insight" />
      {/* <Link to="/dealdashboard/funding" className="text-gray-800">
        Back
      </Link> */}

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex justify-center items-center bg-gray-500 p-2 text-white rounded sticky -top-[15px]"
      >
        <FaArrowLeft />
        &nbsp; Back
      </button>

      {funding365Data && (
        <div className="md:w-[90%] mx-auto my-6 p-6 bg-white shadow-md rounded-lg">
          <div className="flex items-center mb-4">
            <img
              src={funding365Data.companyLogoUrl}
              alt="company logo"
              className="w-16 h-16 object-contain mr-4"
            />
            <div>
              <h3 className="text-xl font-aptos-semibold">
                {funding365Data.companyName}
              </h3>
              <h4 className="text-gray-600">CIN: {funding365Data.cin}</h4>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-700">
                <strong>Created By:</strong> {funding365Data?.createAdmin}
              </p>
              <p className="text-gray-700">
                <strong>Created At:</strong>{' '}
                {handleDate(funding365Data.createdAt)}
              </p>
            </div>

            {funding365Data.updateAdmin && (
              <div>
                <p className="text-gray-700">
                  <strong>Updated By:</strong> {funding365Data?.updateAdmin}
                </p>
                <p className="text-gray-700">
                  <strong>Updated At:</strong>{' '}
                  {handleDate(funding365Data.updatedAt)}
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-start items-center gap-5">
            <Link
              to={`/dealdashboard/funding/edit/company/${funding365Data.companyId}`}
              className="flex justify-center items-center bg-orange-500 text-white p-2 rounded hover:bg-orange-600 max-w-[100px]"
              title="Edit"
            >
              <FaEdit />
            </Link>
            <button
              type="button"
              onClick={() => {
                handleDeleteClick(
                  funding365Data.companyId,
                  funding365Data.companyName
                )
                setDeletingCompany(true)
              }}
              className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                sendingMail || deleteMutation.isPending
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
              title="Hide"
              disabled={sendingMail || deleteMutation.isPending}
            >
              {sendingMail && toDelete === funding365Data.companyId ? (
                <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
              ) : (
                <FaEyeSlash />
              )}
            </button>
          </div>
        </div>
      )}

      <div className="md:w-[90%] mx-auto flex justify-between items-center">
        <h2
          className={`text-2xl font-aptos-semibold ${
            showHidden ? 'text-red-600' : 'text-gray-600'
          }`}
        >
          {showHidden ? 'Hidden' : 'Active'} Files
        </h2>

        <button
          onClick={handleToggle}
          title={showHidden ? 'Show active reports' : 'Show hidden reports'}
          className="text-white bg-blue-500 p-2 rounded hover:bg-blue-700 transition-colors"
        >
          {showHidden ? <FaEye /> : <FaEyeSlash />}
        </button>
      </div>
      <div className="md:w-[90%] mx-auto my-6 overflow-x-auto whitespace-nowrap">
        <table className="min-w-full border border-gray-200 text-center shadow-md rounded-lg">
          <thead className="bg-[#E9ECEF] text-gray-700">
            <tr>
              <th className="py-3 px-4 border-b">S No</th>
              <th className="py-3 px-4 border-b">Funding Date</th>
              <th className="py-3 px-4 border-b">Price</th>
              <th className="py-3 px-4 border-b">Pdf</th>
              <th className="py-3 px-4 border-b">Funding Details</th>
              <th className="py-3 px-4 border-b">Created At</th>
              <th className="py-3 px-4 border-b">Created By</th>
              <th className="py-3 px-4 border-b">Updated At</th>
              <th className="py-3 px-4 border-b">Updated By</th>
              <th className="py-3 px-4 border-b">Hide At</th>
              <th className="py-3 px-4 border-b">Hide By</th>
              <th className="py-3 px-4 border-b">Action</th>
            </tr>
          </thead>
          <tbody>
            {funding365Data.fundingList &&
              funding365Data.fundingList.map((item, index) => (
                <tr
                  key={item.id}
                  className="hover:bg-gray-50 text-gray-700 transition-colors duration-200"
                >
                  <td className="py-3 px-4 border-b">{index + 1}</td>

                  <td className="py-3 px-4 border-b">{item.fundingDate}</td>
                  <td className="py-3 px-4 border-b">&#8377;{item.pdfPrice}</td>
                  <td className="py-3 px-4 border-b">
                    {getCleanPdfName(item.pdfName)}
                  </td>
                  <td className="py-3 px-4 border-b max-w-lg">
                    <div className="truncate" title={item.fundingDetails}>
                      {item.fundingDetails}
                    </div>
                  </td>
                  <td className="py-3 px-4 border-b">
                    {handleDate(item.createdAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.createAdmin}</td>
                  <td className="py-3 px-4 border-b">
                    {handleDate(item.updatedAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.updateAdmin}</td>
                  <td className="py-3 px-4 border-b">
                    {handleDate(item?.deletedAt)}
                  </td>
                  <td className="py-3 px-4 border-b">{item?.deleteAdmin}</td>
                  <td className="py-3 px-4 border-b">
                    <div className="flex justify-center space-x-2">
                      {item?.deleted === false && (
                        <Link
                          to={`/dealdashboard/edit/funding/${funding365Data.companyId}/${item.id}`}
                          className="bg-orange-400 text-white p-2 rounded-md hover:bg-orange-500"
                          title="Edit"
                        >
                          <FaEdit />
                        </Link>
                      )}

                      {item?.deleted === true ? (
                        <button
                          title="Restore report"
                          onClick={() => restoreMutation.mutate(item.id)}
                          className="text-white bg-green-700 p-2 rounded hover:bg-green-900 transition-colors"
                        >
                          <FaEye />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            handleDeleteClick(
                              item.id,
                              getCleanPdfName(item.pdfName)
                            )
                          }
                          className={`text-white bg-red-500 p-2 rounded hover:bg-red-700 transition-colors ${
                            sendingMail || deleteMutation.isPending
                              ? 'opacity-50 cursor-not-allowed'
                              : ''
                          }`}
                          title="Hide"
                          disabled={sendingMail || deleteMutation.isPending}
                        >
                          {sendingMail && toDelete === item.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                          ) : (
                            <FaEyeSlash />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        {/* Loading overlay for sending email */}
        {sendingMail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-lg font-semibold">
                Sending verification email...
              </p>
              <p className="text-sm text-gray-500 mt-1">Please wait</p>
            </div>
          </div>
        )}
      </div>

      {/* <DsjInsight data={funding365Data} /> */}
      {showHidden ? (
        ''
      ) : (
        <AddItem
          redirect={`/dealdashboard/add/funding/${funding365Data.companyId}`}
        />
      )}
    </>
  )
}

export default FundingReport
